import { useState } from "react";
import { Footprints } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authApi } from "@/context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);

export const CheckinButton = ({ slug, clubName, className = "" }) => {
  const { user, signIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [miles, setMiles] = useState("");
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);

  const onClick = () => {
    if (!user) {
      toast("SIGN IN TO LOG RUNS", {
        description: "Build your mileage history with every club you run with.",
        action: { label: "SIGN IN", onClick: signIn },
      });
      return;
    }
    setOpen((o) => !o);
  };

  const submit = async (e) => {
    e.preventDefault();
    const m = miles === "" ? null : parseFloat(miles);
    if (m !== null && (Number.isNaN(m) || m <= 0 || m > 100)) {
      toast.error("Miles should be a number between 0 and 100.");
      return;
    }
    setBusy(true);
    try {
      await authApi.post("/me/checkins", { club_slug: slug, date, miles: m });
      toast.success(`Logged. Run with ${clubName} is on your record.`);
      setOpen(false);
      setMiles("");
      setDate(today());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log your run. Try again.");
    }
    setBusy(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`checkin-button-${slug}`}
        onClick={onClick}
        className={className}
      >
        <Footprints size={14} /> I RAN WITH THIS CLUB
      </button>
      {open && (
        <form
          onSubmit={submit}
          data-testid="checkin-form"
          className="absolute left-0 top-full mt-2 z-30 w-72 bg-night border border-white/20 shadow-2xl p-5"
        >
          <p className="font-mono-label text-white/50">LOG THIS RUN</p>
          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor="checkin-date" className="font-mono-label text-white/40 block mb-1.5">DATE</label>
              <input
                id="checkin-date"
                data-testid="checkin-date-input"
                type="date"
                value={date}
                max={today()}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-transparent border border-white/20 px-3 py-2.5 text-sm text-white focus:border-white outline-none [color-scheme:dark]"
              />
            </div>
            <div>
              <label htmlFor="checkin-miles" className="font-mono-label text-white/40 block mb-1.5">MILES (OPTIONAL)</label>
              <input
                id="checkin-miles"
                data-testid="checkin-miles-input"
                type="number"
                step="0.1"
                min="0.1"
                max="100"
                placeholder="3.1"
                value={miles}
                onChange={(e) => setMiles(e.target.value)}
                className="w-full bg-transparent border border-white/20 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-white outline-none"
              />
            </div>
          </div>
          <button
            type="submit"
            data-testid="checkin-submit"
            disabled={busy}
            className="btn-invert w-full mt-4 bg-white text-ink font-mono-label px-4 py-3 border border-white hover:bg-transparent hover:text-white disabled:opacity-50"
          >
            {busy ? "LOGGING..." : "LOG RUN"}
          </button>
        </form>
      )}
    </div>
  );
};
